import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import PageTableHeader from "../../../../components/pageTableHeader/pageTableHeader";
import { getTeamDetail, updateTeam } from "../../../../../services/workspace/team.service";
import toast from "react-hot-toast";
import moment from "moment";
import NoData from "../../../../components/noData/noData";
import DefaultButton from "../../../../components/buttons/defaultButton";
import { Icons } from "../../../../../utils/icons";
import DefaultSearchSelectInput from "../../../../components/inputs/defaultSearchSelectInput";
import { useFilterOptions } from "../../../../../hooks";
import { TbEdit } from "react-icons/tb";
import { IoMdClose } from "react-icons/io";
import { useDispatch, useSelector } from "react-redux";
import { fetchStaffList } from "../../../../../store/slices/user.slice";
import { usePermissionCheck } from "../../../../../hooks/usePermissionCheck";

const items = [
  { label: "Basic Setup", path: "/configuration" },
  { label: "Manage Team", path: "/configuration/manage-team" },
  { label: "Team Detail", path: "" },
];

// Inline Editable Component
const EditableField = ({ value, onSave, label, icon, isLoading = false, disabled = false }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  useEffect(() => {
    setEditValue(value);
  }, [value]);

  const handleSave = () => {
    if (editValue.trim() !== value && editValue.trim() !== "") {
      onSave(editValue.trim());
    } else {
      setEditValue(value); // Reset if no change
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditValue(value);
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSave();
    } else if (e.key === "Escape") {
      e.preventDefault();
      handleCancel();
    }
  };

  const handleBlur = () => {
    handleSave();
  };

  if (isEditing) {
    return (
      <div className="grid grid-cols-2 gap-4 py-2 border-b border-gray-100 last:border-b-0">
        <div className="flex items-center gap-2">
          {icon && <span className="text-gray-500">{icon}</span>}
          <span className="text-sm text-gray-600 font-medium">{label}</span>
        </div>
        <div className="flex items-center gap-2">
          <input
            ref={inputRef}
            type="text"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            disabled={isLoading}
            className="flex-1 px-2 py-1 text-sm font-semibold text-theme border border-theme rounded focus:outline-none focus:ring-2 focus:ring-theme"
          />
          {isLoading && (
            <span className="text-xs text-gray-500">Saving...</span>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-4 py-2 border-b border-gray-100 last:border-b-0">
      <div className="flex items-center gap-2">
        {icon && <span className="text-gray-500">{icon}</span>}
        <span className="text-sm text-gray-600 font-medium">{label}</span>
      </div>
      <span
        className={`text-sm font-semibold text-theme px-2 py-1 rounded transition-colors ${
          disabled 
            ? "cursor-not-allowed opacity-50" 
            : "cursor-pointer hover:bg-gray-50"
        }`}
        onClick={() => !disabled && setIsEditing(true)}
        title={disabled ? "You don't have permission to edit" : "Click to edit"}
      >
        {value || <span className="text-gray-400">-</span>}
      </span>
    </div>
  );
};

// Editable Select Field Component (for Department)
const EditableSelectField = ({ value, options, onSave, label, icon, isLoading = false, placeholder = "Select...", displayValue = null, disabled = false }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [selectedValue, setSelectedValue] = useState(null);

  useEffect(() => {
    // Find the current value in options
    if (value != null && options && options.length > 0) {
      const found = options.find(opt => opt.value === value || opt.label === value);
      setSelectedValue(found || null);
    } else {
      setSelectedValue(null);
    }
  }, [value, options]);

  const handleSave = (newValue) => {
    // Allow null/undefined to clear selection (for optional fields like team lead)
    if (newValue === null || newValue === undefined) {
      onSave(null);
    } else if (newValue && newValue.value !== value) {
      onSave(newValue.value);
    } else if (!newValue && value) {
      // If clearing selection and there was a value
      onSave(null);
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <div className="grid grid-cols-2 gap-4 py-2 border-b border-gray-100 last:border-b-0">
        <div className="flex items-center gap-2">
          {icon && <span className="text-gray-500">{icon}</span>}
          <span className="text-sm text-gray-600 font-medium">{label}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex-1">
            <DefaultSearchSelectInput
              value={selectedValue}
              options={options || []}
              onChange={(selected) => {
                // Allow null to clear selection (for optional fields)
                if (selected === null || selected === undefined) {
                  handleSave(null);
                } else if (selected) {
                  handleSave(selected);
                } else {
                  // If user clears selection, save null
                  handleSave(null);
                }
              }}
              placeholder={placeholder}
              isDisabled={isLoading || disabled}
              disabled={isLoading || disabled}
              isMulti={false}
              isClearable={true}
              customWidth="w-full"
              lebelVertical={false}
            />
          </div>
          {isLoading && (
            <span className="text-xs text-gray-500">Saving...</span>
          )}
          <button
            type="button"
            onClick={handleCancel}
            className="p-1 text-gray-500 hover:text-gray-700"
            title="Cancel"
          >
            <IoMdClose className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-4 py-2 border-b border-gray-100 last:border-b-0">
      <div className="flex items-center gap-2">
        {icon && <span className="text-gray-500">{icon}</span>}
        <span className="text-sm text-gray-600 font-medium">{label}</span>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-sm font-semibold text-theme flex-1">
          {displayValue || selectedValue?.label || (value != null ? options?.find(opt => opt.value === value)?.label : null) || <span className="text-gray-400">-</span>}
        </span>
        {!disabled && (
          <button
            type="button"
            onClick={() => setIsEditing(true)}
            className="p-1 text-gray-400 hover:text-theme transition-colors"
            title="Click to edit"
          >
            <TbEdit className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
};

// Reusable DetailCard component
const DetailCard = ({ label, value, icon }) => (
  <div className="grid grid-cols-2 gap-4 py-2 border-b border-gray-100 last:border-b-0">
    <div className="flex items-center gap-2">
      {icon && <span className="text-gray-500">{icon}</span>}
      <span className="text-sm text-gray-600 font-medium">{label}</span>
    </div>
    <span className="text-sm font-semibold text-theme">
      {value || <span className="text-gray-400">-</span>}
    </span>
  </div>
);

// Member Card Component
const MemberCard = ({ member, onRemove, isRemoving = false }) => {
  const user = member?.user || {};
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow relative group">
      {/* Remove Button */}
      {onRemove && (
        <button
          onClick={onRemove}
          disabled={isRemoving}
          className="absolute top-2 right-2 p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full opacity-0 group-hover:opacity-100 transition-all"
          title="Remove from team"
        >
          <IoMdClose className="w-4 h-4" />
        </button>
      )}
      
      <div className="flex items-start gap-4">
        {/* Avatar */}
        <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
          {user.image ? (
            <img
              src={user.image}
              alt={user.name}
              className="w-12 h-12 rounded-full object-cover"
            />
          ) : (
            <span className="text-blue-600 font-semibold text-lg">
              {user.name?.charAt(0)?.toUpperCase() || "U"}
            </span>
          )}
        </div>
        
        {/* Member Info */}
        <div className="flex-1">
          <h3 className="text-theme font-semibold text-base mb-1">
            {user.name || "Unknown"}
          </h3>
          <div className="flex flex-col gap-1 text-sm text-gray-600">
            {user.code && (
              <span>
                <span className="font-medium">Code:</span> {user.code}
              </span>
            )}
            {user.email && (
              <span>
                <span className="font-medium">Email:</span> {user.email}
              </span>
            )}
            {user.phone && (
              <span>
                <span className="font-medium">Phone:</span> {user.country_code || ""} {user.phone}
              </span>
            )}
            {member.created_at && (
              <span className="text-xs text-gray-500">
                Joined: {moment(member.created_at).format("DD MMM YYYY")}
              </span>
            )}
          </div>
        </div>
        
        {/* Status Badge */}
        <div className="mt-6">
          <span
            className={`px-3 py-1 rounded-full text-xs font-medium ${
              member.status === 1
                ? "bg-green-100 text-green-800"
                : "bg-gray-100 text-gray-800"
            }`}
          >
            {member.status === 1 ? "Active" : "Inactive"}
          </span>
        </div>
      </div>
    </div>
  );
};

// Skeleton Loader
const DetailSkeleton = () => (
  <div className="grid grid-cols-2 gap-4 py-2 animate-pulse">
    <div className="h-5 bg-gray-300 rounded w-3/4" />
    <div className="h-5 bg-gray-200 rounded w-2/3" />
  </div>
);

const TeamDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [teamData, setTeamData] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [updatingDepartment, setUpdatingDepartment] = useState(false);
  const [updatingManager, setUpdatingManager] = useState(false);
  const [updatingTeamLead, setUpdatingTeamLead] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [updatingMembers, setUpdatingMembers] = useState(false);
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(null); // Stores member to remove
  
  // Status options
  const statusOptions = [
    { label: "Active", value: 1 },
    { label: "Inactive", value: 0 },
  ];

  // Check team permissions
  const teamPermissions = usePermissionCheck("hr", "team");
  const canUpdate = teamPermissions.canUpdate || teamPermissions.hasAnyPermission;
  const { role: userType } = useSelector((state) => state.user);
  
  // Allow Super Admin (0) and Workspace Admin (1) to edit even without explicit permission
  const canEdit = canUpdate || userType === 0 || userType === 1;
  
  // Fetch departments for dropdown
  const { departments, departmentsLoading, refresh } = useFilterOptions(["departments"]);
  
  // Fetch staff list for team lead dropdown
  const { staffList, staffLoading } = useSelector((state) => state.user);
  
  // Handle different possible structures of staffList
  // Case 1: staffList.user.data (raw API response structure)
  // Case 2: staffList is already an array of label/value objects (from convertToLabelValue)
  // Case 3: staffList is an array of user objects
  const staffOptions = (() => {
    // Check if it's the nested structure (staffList.user.data)
    if (staffList?.user?.data && Array.isArray(staffList.user.data)) {
      return staffList.user.data.map((user) => ({
        label: user?.name || "-",
        value: user?.id || user?.uuid,
      })).filter(staff => staff.value != null);
    }
    
    // Check if it's already in label/value format (from convertToLabelValue)
    if (Array.isArray(staffList)) {
      return staffList.map((staff) => ({
        label: staff?.label || staff?.name || "-",
        value: staff?.value || staff?.id || staff?.uuid,
      })).filter(staff => staff.value != null);
    }
    
    return [];
  })();

  // Fetch staff list on mount
  useEffect(() => {
    dispatch(fetchStaffList({ type: 2, additional_info: true }));
  }, [dispatch]);
  
  // Debug: Log staffList and staffOptions when they change
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
    }
  }, [staffList, teamData]);

  useEffect(() => {
    if (!id) return;
    fetchTeamDetail();
  }, [id]);

  const fetchTeamDetail = () => {
    setLoading(true);
    // Fetch team detail with all relations
    getTeamDetail(id)
      .then((response) => {
        setTeamData(response.team || response);
      })
      .catch((error) => {
        toast.error("Failed to fetch team details!");
        console.error("Error fetching team detail:", error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleUpdateTeamName = async (newName) => {
    if (!newName || newName.trim() === "") {
      toast.error("Team name cannot be empty!");
      return;
    }

    if (newName === teamData?.name) {
      return; // No change
    }

    setUpdating(true);
    try {
      const updateData = {
        name: newName,
        department_id: teamData?.department?.id || teamData?.department_id ? parseInt(teamData?.department?.id || teamData?.department_id, 10) : null,
        manager_id: teamData?.manager?.user?.id || teamData?.manager?.id ? parseInt(teamData?.manager?.user?.id || teamData?.manager?.id, 10) : null,
        leader_id: teamData?.leader?.user?.id || teamData?.leader?.id ? parseInt(teamData?.leader?.user?.id || teamData?.leader?.id, 10) : null,
        members: Array.isArray(teamData?.members)
          ? teamData.members.map((m) => {
              const memberId = m.user?.id || m.id || m;
              return memberId != null ? parseInt(memberId, 10) : null;
            }).filter(id => id != null)
          : [],
      };

      const teamUuid = teamData?.uuid || teamData?.id || id;
      await updateTeam(updateData, teamUuid);
      toast.success("Team name updated successfully!");
      
      // Update local state immediately for better UX
      setTeamData((prev) => ({ ...prev, name: newName }));
    } catch (error) {
      toast.error("Failed to update team name!");
      console.error("Error updating team:", error);
      // Revert to original value on error
      setTeamData((prev) => ({ ...prev, name: teamData?.name }));
    } finally {
      setUpdating(false);
    }
  };

  const handleUpdateDepartment = async (newDepartmentId) => {
    if (!newDepartmentId) {
      toast.error("Please select a department!");
      return;
    }

    const currentDepartmentId = teamData?.department?.id || teamData?.department_id;
    if (newDepartmentId === currentDepartmentId) {
      return; // No change
    }

    setUpdatingDepartment(true);
    try {
      // Convert department ID to integer
      const departmentIdInt = parseInt(newDepartmentId, 10);
      
      const updateData = {
        name: teamData?.name,
        department_id: departmentIdInt,
        manager_id: teamData?.manager?.user?.id || teamData?.manager?.id ? parseInt(teamData?.manager?.user?.id || teamData?.manager?.id, 10) : null,
        leader_id: teamData?.leader?.user?.id || teamData?.leader?.id ? parseInt(teamData?.leader?.user?.id || teamData?.leader?.id, 10) : null,
        members: Array.isArray(teamData?.members)
          ? teamData.members.map((m) => {
              const memberId = m.user?.id || m.id || m;
              return memberId != null ? parseInt(memberId, 10) : null;
            }).filter(id => id != null)
          : [],
      };

      const teamUuid = teamData?.uuid || teamData?.id || id;
      await updateTeam(updateData, teamUuid);
      toast.success("Department updated successfully!");
      
      // Update local state - find department name from options
      const selectedDept = departments.find(d => d.value === newDepartmentId);
      setTeamData((prev) => ({
        ...prev,
        department: {
          ...prev.department,
          id: newDepartmentId,
          name: selectedDept?.label || prev.department?.name,
        },
        department_id: newDepartmentId,
      }));
      
      // Refresh team data to get updated department info
      fetchTeamDetail();
    } catch (error) {
      toast.error("Failed to update department!");
      console.error("Error updating department:", error);
    } finally {
      setUpdatingDepartment(false);
    }
  };

  const handleUpdateManager = async (newManagerId) => {
    // Allow null/empty to remove manager
    const currentManagerId = teamData?.manager?.user?.id || teamData?.manager?.id || null;
    
    // Convert to integer if value exists, otherwise null
    const managerIdInt = newManagerId != null && newManagerId !== '' 
      ? parseInt(newManagerId, 10) 
      : null;
    
    if (managerIdInt === currentManagerId) {
      return; // No change
    }

    setUpdatingManager(true);
    try {
      const updateData = {
        name: teamData?.name,
        department_id: teamData?.department?.id || teamData?.department_id ? parseInt(teamData?.department?.id || teamData?.department_id, 10) : null,
        manager_id: managerIdInt, // Integer or null
        leader_id: teamData?.leader?.user?.id || teamData?.leader?.id ? parseInt(teamData?.leader?.user?.id || teamData?.leader?.id, 10) : null,
        members: Array.isArray(teamData?.members)
          ? teamData.members.map((m) => {
              const memberId = m.user?.id || m.id || m;
              return memberId != null ? parseInt(memberId, 10) : null;
            }).filter(id => id != null)
          : [],
      };

      const teamUuid = teamData?.uuid || teamData?.id || id;
      await updateTeam(updateData, teamUuid);
      toast.success(newManagerId ? "Manager updated successfully!" : "Manager removed successfully!");
      
      // Update local state
      if (newManagerId) {
        const selectedManager = staffOptions.find(s => s.value === newManagerId);
        setTeamData((prev) => ({
          ...prev,
          manager: {
            ...prev.manager,
            user: {
              ...prev.manager?.user,
              id: newManagerId,
              name: selectedManager?.label || prev.manager?.user?.name,
            },
            id: newManagerId,
          },
        }));
      } else {
        // Remove manager
        setTeamData((prev) => ({
          ...prev,
          manager: null,
        }));
      }
      
      // Refresh team data to get updated info
      fetchTeamDetail();
    } catch (error) {
      toast.error("Failed to update manager!");
      console.error("Error updating manager:", error);
    } finally {
      setUpdatingManager(false);
    }
  };

  const handleUpdateTeamLead = async (newLeaderId) => {
    // Allow null/empty to remove team lead
    const currentLeaderId = teamData?.leader?.user?.id || teamData?.leader?.id || null;
    
    // Convert to integer if value exists, otherwise null
    const leaderIdInt = newLeaderId != null && newLeaderId !== '' 
      ? parseInt(newLeaderId, 10) 
      : null;
    
    if (leaderIdInt === currentLeaderId) {
      return; // No change
    }

    setUpdatingTeamLead(true);
    try {
      const updateData = {
        name: teamData?.name,
        department_id: teamData?.department?.id || teamData?.department_id ? parseInt(teamData?.department?.id || teamData?.department_id, 10) : null,
        manager_id: teamData?.manager?.user?.id || teamData?.manager?.id ? parseInt(teamData?.manager?.user?.id || teamData?.manager?.id, 10) : null,
        leader_id: leaderIdInt, // Integer or null
        members: Array.isArray(teamData?.members)
          ? teamData.members.map((m) => {
              const memberId = m.user?.id || m.id || m;
              return memberId != null ? parseInt(memberId, 10) : null;
            }).filter(id => id != null)
          : [],
      };

      const teamUuid = teamData?.uuid || teamData?.id || id;
      await updateTeam(updateData, teamUuid);
      toast.success(newLeaderId ? "Team lead updated successfully!" : "Team lead removed successfully!");
      
      // Update local state
      if (newLeaderId) {
        const selectedLeader = staffOptions.find(s => s.value === newLeaderId);
        setTeamData((prev) => ({
          ...prev,
          leader: {
            ...prev.leader,
            user: {
              ...prev.leader?.user,
              id: newLeaderId,
              name: selectedLeader?.label || prev.leader?.user?.name,
            },
            id: newLeaderId,
          },
        }));
      } else {
        // Remove team lead
        setTeamData((prev) => ({
          ...prev,
          leader: null,
        }));
      }
      
      // Refresh team data to get updated info
      fetchTeamDetail();
    } catch (error) {
      toast.error("Failed to update team lead!");
      console.error("Error updating team lead:", error);
    } finally {
      setUpdatingTeamLead(false);
    }
  };

  const handleUpdateStatus = async (newStatus) => {
    if (newStatus === null || newStatus === undefined) {
      return;
    }
    
    const currentStatus = teamData?.status;
    if (newStatus === currentStatus) {
      return; // No change
    }

    setUpdatingStatus(true);
    try {
      // Convert status to integer
      const statusInt = parseInt(newStatus, 10);
      
      const updateData = {
        name: teamData?.name,
        department_id: teamData?.department?.id || teamData?.department_id ? parseInt(teamData?.department?.id || teamData?.department_id, 10) : null,
        manager_id: teamData?.manager?.user?.id || teamData?.manager?.id ? parseInt(teamData?.manager?.user?.id || teamData?.manager?.id, 10) : null,
        leader_id: teamData?.leader?.user?.id || teamData?.leader?.id ? parseInt(teamData?.leader?.user?.id || teamData?.leader?.id, 10) : null,
        members: Array.isArray(teamData?.members)
          ? teamData.members.map((m) => {
              const memberId = m.user?.id || m.id || m;
              return memberId != null ? parseInt(memberId, 10) : null;
            }).filter(id => id != null)
          : [],
        status: statusInt,
      };

      const teamUuid = teamData?.uuid || teamData?.id || id;
      await updateTeam(updateData, teamUuid);
      toast.success("Status updated successfully!");
      
      // Update local state
      setTeamData((prev) => ({
        ...prev,
        status: newStatus,
      }));
      
      fetchTeamDetail();
    } catch (error) {
      toast.error("Failed to update status!");
      console.error("Error updating status:", error);
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleAddMembers = async () => {
    if (!selectedMembers || selectedMembers.length === 0) {
      toast.error("Please select at least one member!");
      return;
    }

    setUpdatingMembers(true);
    try {
      // Get current member IDs and convert to integers
      const currentMemberIds = Array.isArray(teamData?.members)
        ? teamData.members.map((m) => {
            const memberId = m.user?.id || m.id || m;
            return memberId != null ? parseInt(memberId, 10) : null;
          }).filter(id => id != null)
        : [];
      
      // Add new member IDs (avoid duplicates) and convert to integers
      const newMemberIds = selectedMembers.map(m => {
        const memberId = m.value || m;
        return memberId != null ? parseInt(memberId, 10) : null;
      }).filter(id => id != null);
      const allMemberIds = [...new Set([...currentMemberIds, ...newMemberIds])];

      const updateData = {
        name: teamData?.name,
        department_id: teamData?.department?.id || teamData?.department_id ? parseInt(teamData?.department?.id || teamData?.department_id, 10) : null,
        manager_id: teamData?.manager?.user?.id || teamData?.manager?.id ? parseInt(teamData?.manager?.user?.id || teamData?.manager?.id, 10) : null,
        leader_id: teamData?.leader?.user?.id || teamData?.leader?.id ? parseInt(teamData?.leader?.user?.id || teamData?.leader?.id, 10) : null,
        members: allMemberIds,
        status: teamData?.status ? parseInt(teamData?.status, 10) : null,
      };

      const teamUuid = teamData?.uuid || teamData?.id || id;
      await updateTeam(updateData, teamUuid);
      toast.success("Members added successfully!");
      setShowAddMemberModal(false);
      setSelectedMembers([]);
      fetchTeamDetail();
    } catch (error) {
      toast.error("Failed to add members!");
      console.error("Error adding members:", error);
    } finally {
      setUpdatingMembers(false);
    }
  };

  const handleRemoveMember = async () => {
    if (!showRemoveConfirm) return;
    
    const memberId = showRemoveConfirm.user?.id || showRemoveConfirm.id;
    if (!memberId) return;
    
    // Convert member ID to integer
    const memberIdInt = parseInt(memberId, 10);
    if (isNaN(memberIdInt)) {
      toast.error("Invalid member ID!");
      return;
    }

    setUpdatingMembers(true);
    try {
      // Get current member IDs and convert to integers, then remove the specified one
      const currentMemberIds = Array.isArray(teamData?.members)
        ? teamData.members.map((m) => {
            const id = m.user?.id || m.id || m;
            return id != null ? parseInt(id, 10) : null;
          }).filter(id => id != null)
        : [];
      
      const updatedMemberIds = currentMemberIds.filter(id => id !== memberIdInt);

      const updateData = {
        name: teamData?.name,
        department_id: teamData?.department?.id || teamData?.department_id ? parseInt(teamData?.department?.id || teamData?.department_id, 10) : null,
        manager_id: teamData?.manager?.user?.id || teamData?.manager?.id ? parseInt(teamData?.manager?.user?.id || teamData?.manager?.id, 10) : null,
        leader_id: teamData?.leader?.user?.id || teamData?.leader?.id ? parseInt(teamData?.leader?.user?.id || teamData?.leader?.id, 10) : null,
        members: updatedMemberIds,
        status: teamData?.status ? parseInt(teamData?.status, 10) : null,
      };

      const teamUuid = teamData?.uuid || teamData?.id || id;
      await updateTeam(updateData, teamUuid);
      toast.success("Member removed successfully!");
      setShowRemoveConfirm(null);
      fetchTeamDetail();
    } catch (error) {
      toast.error("Failed to remove member!");
      console.error("Error removing member:", error);
    } finally {
      setUpdatingMembers(false);
    }
  };

  // Get available staff (not already in team)
  const getAvailableStaff = () => {
    if (!Array.isArray(staffOptions) || staffOptions.length === 0) {
      return [];
    }
    
    const currentMemberIds = Array.isArray(teamData?.members)
      ? teamData.members.map((m) => {
          const memberId = m.user?.id || m.id || m;
          // Convert to string for comparison to handle both int and string IDs
          return memberId != null ? String(memberId) : null;
        }).filter(id => id != null)
      : [];
    
    // Filter out staff who are already members, comparing as strings
    return staffOptions.filter(staff => {
      const staffId = staff.value != null ? String(staff.value) : null;
      return staffId && !currentMemberIds.includes(staffId);
    });
  };

  if (loading) {
    return (
      <div className="flex flex-col gap-2">
        <PageTableHeader breadcrumbs={items} />
        <div className="bg-white p-6 rounded-lg">
          <div className="flex flex-col gap-4">
            {Array.from({ length: 6 }).map((_, idx) => (
              <DetailSkeleton key={idx} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!teamData) {
    return (
      <div className="flex flex-col gap-2">
        <PageTableHeader breadcrumbs={items} />
        <div className="bg-white p-6 rounded-lg">
          <NoData
            title="Team Not Found"
            message="The team you're looking for doesn't exist or has been removed."
            buttonText="Go Back"
            onClick={() => navigate("/configuration/manage-team")}
          />
        </div>
      </div>
    );
  }

  // Basic Details
  const basicDetails = [
    {
      label: "Team Name",
      value: teamData?.name,
      icon: "👥",
    },
    {
      label: "Department",
      value: teamData?.department?.name || "-",
      icon: "🏢",
    },
    {
      label: "Manager",
      value: teamData?.manager?.user?.name || teamData?.manager?.name || "No manager assigned",
      icon: "👔",
    },
    {
      label: "Team Lead",
      value: teamData?.leader?.user?.name || teamData?.leader?.name || "No team lead assigned",
      icon: "👤",
    },
    {
      label: "Total Members",
      value: teamData?.total_members || teamData?.members?.length || 0,
      icon: "👨‍👩‍👧‍👦",
    },
    {
      label: "Status",
      value: teamData?.status === 1 ? "Active" : "Inactive",
      icon: "📊",
    },
    {
      label: "Created At",
      value: teamData?.created_at
        ? moment(teamData.created_at).format("DD MMM YYYY, hh:mm A")
        : "-",
      icon: "📅",
    },
  ];

  // Description
  const hasDescription = teamData?.description;

  // Members List
  const members = teamData?.members || [];

  return (
    <div className="flex flex-col gap-4">
      <PageTableHeader
        breadcrumbs={items}
        isSearch={false}
        filter={false}
        shorting={false}
        buttons={
            <DefaultButton
              icon={Icons.EDIT}
              onClick={() => navigate(`/configuration/manage-team`)}
              variant="primary"
            />
        }
      />

      <div className="flex flex-col gap-4">
        {/* Basic Details Card */}
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="border-b border-gray-200 px-6 py-4 flex justify-between items-center">
            <h2 className="text-lg font-semibold text-theme">Basic Details</h2>
           
          </div>
          <div className="p-6 ">
            <div className="flex flex-col gap-2">
              {/* Team Name - Editable */}
              <EditableField
                value={teamData?.name || ""}
                onSave={handleUpdateTeamName}
                label="Team Name"
                icon="👥"
                isLoading={updating}
                disabled={!canEdit}
              />
              
              {/* Department - Editable with Dropdown */}
              <EditableSelectField
                value={teamData?.department?.id || teamData?.department_id}
                options={departments || []}
                onSave={handleUpdateDepartment}
                label="Department"
                icon="🏢"
                isLoading={updatingDepartment || departmentsLoading}
                placeholder="Select department..."
                disabled={!canEdit}
              />
              
              {/* Manager - Editable with Dropdown */}
              <EditableSelectField
                value={teamData?.manager?.user?.id || teamData?.manager?.id || null}
                options={staffOptions || []}
                onSave={handleUpdateManager}
                label="Manager"
                icon="👔"
                isLoading={updatingManager || staffLoading}
                placeholder="Select manager (optional)..."
                displayValue={teamData?.manager?.user?.name || teamData?.manager?.name || "No manager assigned"}
                disabled={!canEdit}
              />
              
              {/* Team Lead - Editable with Dropdown */}
              <EditableSelectField
                value={teamData?.leader?.user?.id || teamData?.leader?.id || null}
                options={staffOptions || []}
                onSave={handleUpdateTeamLead}
                label="Team Lead"
                icon="👤"
                isLoading={updatingTeamLead || staffLoading}
                placeholder="Select team lead (optional)..."
                displayValue={teamData?.leader?.user?.name || teamData?.leader?.name || "No team lead assigned"}
                disabled={!canEdit}
              />
              
              {/* Total Members - Non-editable */}
              <DetailCard
                label="Total Members"
                value={teamData?.total_members || teamData?.members?.length || 0}
                icon="👨‍👩‍👧‍👦"
              />
              
              {/* Status - Editable with Dropdown */}
              <EditableSelectField
                value={teamData?.status}
                options={statusOptions}
                onSave={handleUpdateStatus}
                label="Status"
                icon="📊"
                isLoading={updatingStatus}
                placeholder="Select status..."
                displayValue={teamData?.status === 1 ? "Active" : "Inactive"}
                disabled={!canEdit}
              />
              
              {/* Created At - Non-editable */}
              <DetailCard
                label="Created At"
                value={teamData?.created_at ? moment(teamData.created_at).format("DD MMM YYYY, hh:mm A") : "-"}
                icon="📅"
              />
            </div>
          </div>
        </div>

        {/* Description Card */}
        {hasDescription && (
          <div className="bg-white rounded-lg shadow-sm">
            <div className="border-b border-gray-200 px-6 py-4">
              <h2 className="text-lg font-semibold text-theme">Description</h2>
            </div>
            <div className="p-6">
              <p className="text-sm text-gray-700 leading-relaxed">
                {teamData.description}
              </p>
            </div>
          </div>
        )}

        {/* Team Members Card */}
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="border-b border-gray-200 px-6 py-4 flex justify-between items-center">
            <h2 className="text-lg font-semibold text-theme">
              Team Members ({members.length})
            </h2>
            <DefaultButton
              icon={Icons.ADD}
              onClick={() => setShowAddMemberModal(true)}
              variant="primary"
              size="sm"
              disabled={updatingMembers || !canEdit}
            >
              Add Member
            </DefaultButton>
          </div>
          <div className="p-6">
            {members.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {members.map((member, idx) => (
                  <MemberCard 
                    key={member.id || member.uuid || idx} 
                    member={member} 
                    onRemove={canEdit ? () => setShowRemoveConfirm(member) : null}
                    isRemoving={updatingMembers}
                  />
                ))}
              </div>
            ) : (
              <div className="py-8">
                <NoData
                  title="No Members"
                  message="This team doesn't have any members yet."
                  buttonText="Add Member"
                  onClick={() => setShowAddMemberModal(true)}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add Member Modal - Theme Styled */}
      {showAddMemberModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg transform transition-all">
            {/* Header */}
            <div className="flex justify-between items-center px-6 py-5 border-b border-gray-100 bg-gradient-to-r from-theme/5 to-transparent rounded-t-xl">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-theme/10 flex items-center justify-center">
                  <span className="text-xl">👥</span>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-theme">Add Team Members</h3>
                  <p className="text-xs text-gray-500">Select members to add to this team</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowAddMemberModal(false);
                  setSelectedMembers([]);
                }}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
              >
                <IoMdClose className="w-5 h-5" />
              </button>
            </div>
            
            {/* Body */}
            <div className="p-6">
              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Select Members
                </label>
                <DefaultSearchSelectInput
                  value={selectedMembers}
                  options={getAvailableStaff()}
                  onChange={(selected) => setSelectedMembers(selected || [])}
                  placeholder={staffLoading ? "Loading staff..." : "Search and select members..."}
                  isDisabled={staffLoading}
                  disabled={staffLoading}
                  isMulti={true}
                  customWidth="w-full"
                  lebelVertical={false}
                />
              </div>
              
              {/* Selected Count */}
              {selectedMembers.length > 0 && (
                <div className="flex items-center gap-2 text-sm text-theme bg-theme/5 px-3 py-2 rounded-lg">
                  <span className="font-medium">{selectedMembers.length}</span>
                  <span>member{selectedMembers.length > 1 ? 's' : ''} selected</span>
                </div>
              )}
              
              {getAvailableStaff().length === 0 && !staffLoading && (
                <div className="flex items-center gap-2 text-sm text-amber-600 bg-amber-50 px-4 py-3 rounded-lg">
                  <span>⚠️</span>
                  <span>All available staff members are already in this team.</span>
                </div>
              )}
            </div>
            
            {/* Footer */}
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50/50 rounded-b-xl">
              <DefaultButton
                name="Cancel"
                onClick={() => {
                  setShowAddMemberModal(false);
                  setSelectedMembers([]);
                }}
                variant="outline"
              />
              <DefaultButton
                name={updatingMembers ? "Adding..." : `Add ${selectedMembers.length > 0 ? selectedMembers.length : ''} Member${selectedMembers.length !== 1 ? 's' : ''}`}
                onClick={handleAddMembers}
                variant="primary"
                disabled={updatingMembers || selectedMembers.length === 0}
              />
            </div>
          </div>
        </div>
      )}

      {/* Remove Confirmation Modal */}
      {showRemoveConfirm && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm transform transition-all">
            {/* Header */}
            <div className="px-6 py-5 text-center">
              <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl">⚠️</span>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Remove Member?</h3>
              <p className="text-sm text-gray-500">
                Are you sure you want to remove{' '}
                <span className="font-semibold text-theme">
                  {showRemoveConfirm?.user?.name || showRemoveConfirm?.name || 'this member'}
                </span>{' '}
                from the team?
              </p>
            </div>
            
            {/* Footer */}
            <div className="flex gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50/50 rounded-b-xl">
              <button
                onClick={() => setShowRemoveConfirm(null)}
                disabled={updatingMembers}
                className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                No, Cancel
              </button>
              <button
                onClick={handleRemoveMember}
                disabled={updatingMembers}
                className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {updatingMembers ? "Removing..." : "Yes, Remove"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamDetail;

